import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/checkout/status/${sessionId}`,
          
        );

        setPaymentData(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return true;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error checking status:', error);
        if (attempts >= 4) {
          setStatus('error');
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      const done = await pollStatus();
      if (!done && attempts < 5) {
        setAttempts(prev => prev + 1);
        setTimeout(poll, 2000);
      }
    };

    poll();
  }, [sessionId, attempts]);

  const formatPrice = (cents) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(cents / 100);
  };

  const getLicenseLabel = (type) => {
    const labels = {
      sync: 'Sync License',
      commercial: 'Commercial License',
      nft: 'NFT/Token License'
    };
    return labels[type] || type;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-medium text-white mb-2">Processing Payment</h2>
          <p className="text-slate-400">Please wait while we confirm your purchase...</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-medium text-white mb-4">
            {status === 'expired' ? 'Session Expired' : 'Payment Error'}
          </h1>
          <p className="text-slate-400 mb-8">
            {status === 'expired'
              ? 'Your payment session has expired. Please try again.'
              : 'Something went wrong while processing your payment.'}
          </p>
          <Button
            onClick={() => navigate('/marketplace')}
            className="bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold rounded-lg"
          >
            Back to Marketplace
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-dark pt-24 flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-medium text-white mb-2" data-testid="success-title">
            Purchase Successful!
          </h1>
          <p className="text-slate-400">
            Thank you for your purchase. Your license is now active.
          </p>
        </div>

        {/* Purchase Details */}
        {paymentData?.metadata && (
          <div className="bg-gradient-card border border-white/10 p-6 rounded-xl mb-8">
            <h2 className="text-lg font-medium text-white mb-4">Purchase Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Track</span>
                <span className="text-white">{paymentData.metadata.track_title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Artist</span>
                <span className="text-white">{paymentData.metadata.artist_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">License</span>
                <span className="text-emerald-400">{getLicenseLabel(paymentData.metadata.license_type)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-white/10">
                <span className="text-slate-400">Amount Paid</span>
                <span className="text-xl font-semibold text-white">
                  {formatPrice(paymentData.amount_total)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <Button
            onClick={() => navigate('/purchases')}
            className="w-full bg-emerald-500 text-slate-900 hover:bg-emerald-400 font-semibold py-6 rounded-lg"
            data-testid="view-licenses-btn"
          >
            <Download className="w-5 h-5 mr-2" />
            View My Licenses
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/marketplace')}
            className="w-full border-slate-700 text-white hover:bg-slate-800 py-6 rounded-lg"
          >
            Continue Browsing
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};
